import is from '@sindresorhus/is';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { useEffect } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useHasRole } from '~/shared/context/use-has-role';
import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { Skeleton } from '~/ui/core/skeleton/Skeleton';
import { Tile } from '~/ui/core/tile/Tile';

import type { ParsedUrlTile, UrlTileRenderContext } from '../types';
import { useOptionalTileAvailability } from './tile-availability-context';
import type { TileAvailability, TilePermissionResolver, TilePermissionResolvers } from './types';

const TileAccessSkeleton: FunctionComponent = () => (
    <Tile className="p-6" data-testid="url-tile-access-skeleton">
        <div className="flex w-full flex-col gap-4">
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-2/3" />
        </div>
    </Tile>
);

const TileAccessDenied: FunctionComponent<{
    readonly onClose: () => void;
    readonly tileType: string;
}> = ({ onClose, tileType }) => {
    const translate = useTranslate();
    const { logger } = useApplicationContext();

    useEffect(() => {
        logger?.warn(`[url-tiles] access denied for tile type "${tileType}"`);
    }, [logger, tileType]);

    return (
        <Tile className="p-6" data-testid="url-tile-access-denied">
            <div className="m-auto flex w-full max-w-[20rem] flex-col items-center gap-4 text-center">
                <p className="m-0 text-base font-semibold text-black-700">
                    {translate('components.url-tile-access.denied-title')}
                </p>
                <p className="m-0 text-sm text-black-400">
                    {translate('components.url-tile-access.denied-description')}
                </p>
                <Button variant="secondary" onClick={onClose}>
                    {translate('common.close')}
                </Button>
            </div>
        </Tile>
    );
};

const TilePermissionGate: FunctionComponent<
    PropsWithChildren<{
        readonly onClose: () => void;
        readonly resolver: TilePermissionResolver;
        readonly tileType: string;
    }>
> = ({ children, onClose, resolver, tileType }) => {
    const { isAllowed, isLoading } = resolver();

    if (isLoading) {
        return <TileAccessSkeleton />;
    }

    if (!isAllowed) {
        return <TileAccessDenied onClose={onClose} tileType={tileType} />;
    }

    return children;
};

const TileAccessControl: FunctionComponent<
    PropsWithChildren<{
        readonly availability: TileAvailability;
        readonly context: UrlTileRenderContext;
        readonly permissionResolvers: TilePermissionResolvers;
        readonly tile: ParsedUrlTile;
    }>
> = ({ availability, children, context, permissionResolvers, tile }) => {
    const requiredRoles = availability.requiredRoles ?? [];
    const hasRequiredRole = useHasRole(...requiredRoles);
    const isRoleAllowed = is.emptyArray(requiredRoles) || hasRequiredRole;
    const onClose = () => context.closeTile(tile.slotKey);

    if (!isRoleAllowed) {
        return <TileAccessDenied onClose={onClose} tileType={tile.definition.type} />;
    }

    if (is.undefined(availability.permission)) {
        return children;
    }

    const resolver = permissionResolvers[availability.permission];

    if (is.undefined(resolver)) {
        return <TileAccessDenied onClose={onClose} tileType={tile.definition.type} />;
    }

    return (
        <TilePermissionGate
            key={availability.permission}
            onClose={onClose}
            resolver={resolver}
            tileType={tile.definition.type}
        >
            {children}
        </TilePermissionGate>
    );
};

export const TileAccessGuard: FunctionComponent<
    PropsWithChildren<{
        readonly context: UrlTileRenderContext;
        readonly tile: ParsedUrlTile;
    }>
> = ({ children, context, tile }) => {
    const availabilityContext = useOptionalTileAvailability();

    if (is.null(availabilityContext)) {
        return children;
    }

    const availability = availabilityContext.availabilityByType.get(tile.definition.type);

    if (is.undefined(availability)) {
        return <TileAccessDenied onClose={() => context.closeTile(tile.slotKey)} tileType={tile.definition.type} />;
    }

    return (
        <TileAccessControl
            availability={availability}
            context={context}
            permissionResolvers={availabilityContext.permissionResolvers}
            tile={tile}
        >
            {children}
        </TileAccessControl>
    );
};
